export interface PasswordRequirement {
  id: string;
  label: string;
  validator: (password: string) => boolean;
}

export interface PasswordStrength {
  isValid: boolean;
  strength: 'weak' | 'medium' | 'strong';
  score: number;
  requirements: {
    requirement: PasswordRequirement;
    satisfied: boolean;
  }[];
  message?: string;
}

export class PasswordHelper {
  // No password requirements - accepts any password
  public static readonly REQUIREMENTS: PasswordRequirement[] = [];

  // No complex password pattern - accepts any password
  public static readonly COMPLEX_PASSWORD_PATTERN = /.*/;

  /**
   * Validate password (no restrictions - accepts any password)
   */
  public static validatePassword(password: string): PasswordStrength {
    // Only check if password is not empty
    const isValid = !!(password && password.trim().length > 0);
    
    return {
      isValid,
      strength: 'strong', // Always show as strong since we accept any password
      score: 5, // Maximum score
      requirements: [], // No requirements
      message: isValid ? undefined : 'Password cannot be empty'
    };
  }

  /**
   * Check if password matches confirmation
   */
  public static validateConfirmPassword(password: string, confirmPassword: string): boolean {
    return password === confirmPassword;
  }

  /**
   * Get password strength color for UI
   */
  public static getStrengthColor(strength: 'weak' | 'medium' | 'strong'): string {
    switch (strength) {
      case 'weak': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'strong': return '#52c41a';
      default: return '#d9d9d9';
    }
  }

  /**
   * Get password strength text for UI
   */
  public static getStrengthText(strength: 'weak' | 'medium' | 'strong'): string {
    switch (strength) {
      case 'weak': return 'Weak';
      case 'medium': return 'Medium';
      case 'strong': return 'Strong';
      default: return '';
    }
  }

  /**
   * Get password requirements as formatted text for UI
   */
  public static getRequirementsText(): string[] {
    return this.REQUIREMENTS.map(req => req.label);
  }

  /**
   * Ant Design form validator for password (no format restrictions)
   */
  public static antdPasswordValidator = (_: any, value: string) => {
    if (!value || value.trim().length === 0) {
      return Promise.reject('Please enter a password');
    }

    // No format validation - accept any non-empty password
    return Promise.resolve();
  };

  /**
   * Ant Design form validator for confirm password
   */
  public static antdConfirmPasswordValidator = (form: any) => (_: any, value: string) => {
    if (!value || value.length === 0) {
      return Promise.reject('Please confirm your password');
    }

    const password = form.getFieldValue('password');
    if (!PasswordHelper.validateConfirmPassword(password, value)) {
      return Promise.reject('Passwords do not match');
    }

    return Promise.resolve();
  };
}